import { DateTime, Duration } from 'luxon';
import { FormApi, formApi } from './form';

export type DateInput = string | number | Date | DateTime;

export function dateFromInput(input: DateInput): Date {
  try {
    const dt =
      input instanceof DateTime
        ? input
        : input instanceof Date
          ? DateTime.fromJSDate(input)
          : DateTime.fromISO((input as any) || new Date().toISOString(), {
            zone: 'utc',
          });
    const local = DateTime.local(dt.year, dt.month, dt.day);
    // console.log('local', input, local);
    return local.toJSDate();
  } catch (er) {
    console.log('Date from input', er);
  }
  return new Date();
}

export function dateTimeFromInput(input: DateInput): DateTime {
  return input instanceof Date
    ? DateTime.fromJSDate(input, { zone: 'utc' })
    : DateTime.fromISO(input as any, { zone: 'utc' });
}
export function daysHoursMinutes(mill: number): string {
  const days = Math.floor(mill / 86400000);
  const hrs = Math.floor((mill % 86400000) / 3600000);
  const min = Math.floor((mill % (1000 * 60 * 60)) / (1000 * 60));
  return (
    (days ? days + 'd ' : '') +
    (hrs ? hrs + 'h ' : ' ') +
    (!days && min ? min + 'm' : '')
  );
}

export function daysHoursMinutesFromNow(input: DateInput): string {
  const then = dateFromInput(input);
  const mill = new Date().getTime() - then.getTime();
  return daysHoursMinutes(mill);
}

export function calculateAge(input: DateInput): number {
  if (!input) {
    return NaN;
  }
  const dob = dateFromInput(input);
  const ms = Date.now() - dob.getTime();
  const age = new Date(ms);

  return Math.abs(age.getUTCFullYear() - 1970);
}

export function ageInYears(
  input: DateInput,
  postFix = '',
  nanDisplay = 'NA'
): string {
  const yrs = calculateAge(input);
  return isNaN(yrs) ? nanDisplay : yrs + postFix;
}

export const defaultDateFormat = 'MM-dd-yyyy';

export function formatDate(
  input: DateInput,
  format = defaultDateFormat
): string {
  if (!input) {
    return '';
  }
  const dt = dateTimeFromInput(input).toLocal();
  return dt.toFormat(format, { timeZone: 'UTC' });
}

export function formatDateTime(
  input: DateInput,
  format = defaultDateFormat
): string {
  if (!input) {
    return '';
  }

  const dt = dateTimeFromInput(input);
  return dt.toFormat(format, { timeZone: 'UTC' });
}


export const defaultDateTimeFormat = 'MM-dd-yyyy HH:mm';
export const messageDateTimeFormat = 'MM-dd-yyyy tt';
export const demographicsDateTimeFormat = 'MM-dd-yyyy HH:mm';
export function formatLocalDate(
  input: DateInput,
  format = defaultDateFormat
): string {
  if (!input) {
    return '';
  }
  let dt: DateTime;
  if (input instanceof Date) {
    dt = DateTime.fromJSDate(input);
  } else {
    let iso = DateTime.fromISO(input as any, { zone: 'utc' });
    dt = DateTime.local(iso.year, iso.month, iso.day);
  }
  return dt.toFormat(format);
}

export function formatLocalDateTime(
  input: DateInput,
  format = defaultDateFormat
): string {
  if (!input) {
    return '';
  }
  let dt: DateTime;

  let iso = DateTime.fromISO(input as any, { zone: 'utc' });
  dt = DateTime.local(iso.year, iso.month, iso.day, iso.hour, iso.minute, iso.second);

  return dt.toFormat(format);
}

export function formatISO(input: DateInput, format = messageDateTimeFormat) {
  if (!input) {
    return '';
  }
  let dt: DateTime;
  if (input instanceof Date) {
    dt = DateTime.fromJSDate(input);
  } else {
    dt = DateTime.fromISO(input as string);
  }
  return dt.toFormat(format);
}
export function stringToDate(input: string) {
  if (!input) {
    return ''
  }

  let a = input.substring(0, 8);

  return formatLocalDate(a);
}

export function utcDateTimeString(input: DateInput): string {
  if (!input) {
    return '';
  }
  let dt: DateTime;
  if (input instanceof Date) {
    dt = DateTime.fromJSDate(input);
    dt = DateTime.utc(dt.year, dt.month, dt.day, dt.hour, dt.minute, dt.second);
  } else {
    dt = DateTime.fromISO(input as any, { zone: 'utc' });
    dt = DateTime.utc(dt.year, dt.month, dt.day, dt.hour, dt.minute, dt.second);
  }

  return dt.toISO();
}

export function utcDateString(input: DateInput): string {
  if (!input) {
    return '';
  }
  let dt: DateTime;
  if (input instanceof Date) {
    dt = DateTime.fromJSDate(input);
    dt = DateTime.utc(dt.year, dt.month, dt.day);
  } else {
    dt = DateTime.fromISO(input as any, { zone: 'utc' });
    dt = DateTime.utc(dt.year, dt.month, dt.day);
  }
  return dt.toISODate();
}

export function utcDate(
  year: number,
  month: number,
  date?: number,
  hours?: number,
  minutes?: number,
  seconds = 0,
  ms = 0
): Date {
  return new Date(Date.UTC(year, month, date, hours, minutes, seconds, ms));
}

export function dateEquals(a: DateInput, b: DateInput): boolean {
  return DateTime.fromJSDate(dateFromInput(a)).equals(
    DateTime.fromJSDate(dateFromInput(b))
  );
}

export function simpleTime(input: DateInput): string {
  const date = dateFromInput(input);
  const dt = DateTime.fromJSDate(date);
  return dt.toLocaleString(DateTime.TIME_SIMPLE as any);
}
export function dateTimeLocal(input: DateInput): string {
  const date = dateFromInput(input);
  const ten = function (i: number) {
    return (i < 10 ? '0' : '') + i;
  };
  const YYYY = date.getFullYear();
  const MM = ten(date.getMonth() + 1);
  const DD = ten(date.getDate());
  const HH = ten(date.getHours());
  const II = ten(date.getMinutes());
  const SS = ten(date.getSeconds());
  return YYYY + '-' + MM + '-' + DD + 'T' + HH + ':' + II + ':' + SS;
}

export function zeroDateTime(input: DateInput): Date {
  const date = dateFromInput(input);
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  return date;
}

export function dateMedium(input: DateInput): string {
  const date = dateFromInput(input);
  const dt = DateTime.fromJSDate(date);
  return dt.toLocaleString(DateTime.DATE_MED as any);
}
export const isoDateString = (input: DateInput): string =>
  formatDate(input, 'yyyy-MM-dd');
export const isoDateTimeString = (input: DateInput): string =>
  DateTime.fromJSDate(dateFromInput(input)).toISO();

const All_DATES = 'All Dates';
const PAST_30_DAYS = 'Last 30 Days';
const THIS_WEEK = 'This Week';
const LAST_WEEK = 'Last Week';
const THIS_MONTH = 'This Month';
const LAST_MONTH = 'Last Month';
const PAST_90_DAYS = 'Past 90 Days';
const YEAR_TO_DATE = 'Year-to-Date';
const LAST_YEAR = 'Last Year';
const CUSTOM = 'Custom Range';

export const dateRangeList = [
  All_DATES,
  PAST_30_DAYS,
  THIS_WEEK,
  LAST_WEEK,
  THIS_MONTH,
  LAST_MONTH,
  PAST_90_DAYS,
  YEAR_TO_DATE,
  LAST_YEAR,
];

export interface DateRange {
  startDate: Date;
  endDate: Date;
  diff?: Duration;
}

export function past(start: Date, days: number): Date {
  const dt = DateTime.fromJSDate(start).minus({ days });
  return dt.toJSDate();
}

export function pastWeeks(start: Date, week: number): Date {
  const dt = DateTime.fromJSDate(start).minus({ week });
  return dt.toJSDate();
}

export function pastMonths(start: Date, month: number): Date {
  const dt = DateTime.fromJSDate(start).minus({ month });
  return dt.toJSDate();
}

export function pastYears(start: Date, year: number): Date {
  const dt = DateTime.fromJSDate(start).minus({ year });
  return dt.toJSDate();
}
function weekAgo(): Date {
  const dt = DateTime.fromJSDate(new Date()).minus({ weeks: 1 });
  return dt.toJSDate();
}
function endOfDay(): Date {
  const dt = DateTime.fromJSDate(new Date()).endOf('day');
  return dt.toJSDate();
}

export function thisWeek(): DateRange {
  const startDate = DateTime.fromJSDate(new Date()).startOf('week').toJSDate();

  const endDate = endOfDay();
  return { startDate, endDate };
}

export function lastWeek(): DateRange {
  const startDate = past(weekAgo(), 7);
  const endDate = endOfDay();

  return { startDate, endDate };
}

export function thisMonth(): DateRange {
  const startDate = DateTime.fromJSDate(new Date())
    .startOf('month')
    .minus({ days: 1 })
    .startOf('month')
    .toJSDate();
  const endDate = endOfDay();

  return { startDate, endDate };
}

export function lastMonth(): DateRange {
  const startDate = DateTime.fromJSDate(new Date()).startOf('month').toJSDate();

  const endDate = endOfDay();

  return { startDate, endDate };
}

export function past30days(): DateRange {
  const startDate = past(new Date(), 30);
  const endDate = endOfDay();
  return { startDate, endDate };
}

export function past90days(): DateRange {
  const startDate = past(new Date(), 90);
  const endDate = new Date();
  return { startDate, endDate };
}

export function yearToDate(): DateRange {
  const startDate = DateTime.fromJSDate(new Date()).startOf('year').toJSDate();
  const endDate = endOfDay();

  return { startDate, endDate };
}

export function dateDiff(
  startDate: DateInput,
  endDate: DateInput,
  bumpEndDate = false
): DateRange {
  const a = DateTime.fromJSDate(dateFromInput(startDate));
  let b = DateTime.fromJSDate(dateFromInput(endDate));

  const diff: Duration = a.diff(b);

  if (bumpEndDate) {
    if (b.toMillis() < a.toMillis()) {
      b = a;
      console.log('set end date a b', a, b);
    }
  }

  return {
    diff,
    startDate: a.toJSDate(),
    endDate: b.toJSDate(),
  };
}

export function lastYear(): DateRange {
  var date: Date = new Date();
  const startDate = new Date(date.getFullYear() - 1, 0, 1);
  const endDate = endOfDay();
  return { startDate, endDate };
}

export function dateRange(type: string): DateRange {
  switch (type) {
    case All_DATES:
      return null;

    case PAST_30_DAYS:
      return past30days();

    case THIS_WEEK:
      return thisWeek();

    case LAST_WEEK:
      return lastWeek();

    case THIS_MONTH:
      return thisMonth();

    case LAST_MONTH:
      return lastMonth();

    case PAST_90_DAYS:
      return past90days();

    case YEAR_TO_DATE:
      return yearToDate();

    case LAST_YEAR:
      return lastYear();

    case CUSTOM:
      return null;
  }
}

type RangeItem = {
  label: string;
  range: DateRange;
};

const dateRangeItems: () => RangeItem[] = () =>
  dateRangeList.map((label) => ({ label, range: dateRange(label) }));

interface DateRangeFormProps {
  label?: string;
  items?: RangeItem[];
  onChange?: (item: RangeItem) => void;
}

interface DateRangeForm {
  range: RangeItem;
}

export function dateRangeForm(
  props: DateRangeFormProps
): FormApi<DateRangeForm> {
  const { label, items, onChange } = props;
  const form = formApi<DateRangeForm>({
    values: {
      range: null,
    },
    fields: {
      range: {
        placeholder: label || 'Date Range',
        list: items || dateRangeItems(),
        listLabel: (v) => v.label,
        onChange: (v) => onChange && onChange(v),
        style: { width: '200px' },
      },
    },
  });
  return form;
}

export const sameDay = (first: Date, second: Date) =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate();


export function validDateObject(d: Date): boolean {
  return d instanceof Date && !isNaN(d.getTime());
}

export function dateMin(a: Date, b: Date): Date {
  return a < b ? a : b;
}